import React, { useEffect, useState } from "react"

import { eachDayOfInterval, format, isAfter, isBefore, startOfDay } from "date-fns"
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Ticket } from "@/hooks/useTickets"
import { cn } from "@/lib/utils"

const getStatusConfig = (status: string | null) => {
  const statusLower = status?.toLowerCase() || ""

  if (statusLower === "approved") {
    return {
      className: "bg-green-100 text-green-800 border-green-200 hover:bg-green-100",
      label: status,
    }
  }
  if (statusLower === "denied") {
    return {
      className: "bg-red-100 text-red-800 border-red-200 hover:bg-red-100",
      label: status,
    }
  }
  if (statusLower === "pending") {
    return {
      className: "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100",
      label: status,
    }
  }
  // Default for unknown statuses
  return {
    className:
      "bg-palette-jungleGreen100 text-palette-jungleGreen600 border-palette-jungleGreen200 hover:bg-palette-jungleGreen100",
    label: status || "-",
  }
}

const StatusBadge: React.FC<{ status: string | null }> = ({ status }) => {
  if (!status) return <span className="text-palette-jungleGreen400">-</span>

  const config = getStatusConfig(status)
  return (
    <Badge variant="outline" className={cn("font-medium", config.className)}>
      {config.label}
    </Badge>
  )
}

interface TicketsTableProps {
  tickets: Ticket[]
  weekStart: Date
  weekEnd: Date
  selectedPeriodStart: Date
  selectedPeriodEnd: Date
}

interface TicketTypeGroup {
  key: string
  label: string
  tickets: Ticket[]
}

interface TicketDateGroup {
  key: string
  label: string
  tickets: Ticket[]
  typeGroups: TicketTypeGroup[]
  isInSelectedPeriod: boolean
}

const DATE_GROUP_LABEL_FORMAT = "EEEE, MMMM d, yyyy"

const getTicketDateGroupMeta = (ticket: Ticket) => {
  if (!ticket.ticket_date_time) {
    return {
      key: "no-ticket-date",
      label: "No ticket date",
      sortValue: Number.NEGATIVE_INFINITY,
    }
  }

  const date = new Date(ticket.ticket_date_time)
  if (Number.isNaN(date.getTime())) {
    return {
      key: "no-ticket-date",
      label: "No ticket date",
      sortValue: Number.NEGATIVE_INFINITY,
    }
  }

  return {
    key: format(date, "yyyy-MM-dd"),
    label: format(date, DATE_GROUP_LABEL_FORMAT),
  }
}

const getTicketTypeGroupMeta = (ticket: Ticket) => {
  const ticketName = ticket.ticket_name?.trim()
  const label = ticketName || "Unknown type"

  return {
    key: ticketName || "unknown-type",
    label,
    isUnknown: !ticketName,
  }
}

const sortTickets = (tickets: Ticket[]) => {
  const decorated = tickets.map((t) => ({
    t,
    time: t.ticket_date_time ? new Date(t.ticket_date_time).getTime() : Number.NEGATIVE_INFINITY,
    createdAt: t.created_at ? new Date(t.created_at).getTime() : Number.NEGATIVE_INFINITY,
  }))
  decorated.sort((a, b) => {
    if (a.time !== b.time) return b.time - a.time
    if (a.createdAt !== b.createdAt) return b.createdAt - a.createdAt
    return a.t.id.localeCompare(b.t.id)
  })
  return decorated.map(({ t }) => t)
}

const buildTypeGroups = (tickets: Ticket[]): TicketTypeGroup[] => {
  const typeGroups = new Map<string, TicketTypeGroup & { isUnknown: boolean }>()

  sortTickets(tickets).forEach((ticket) => {
    const typeMeta = getTicketTypeGroupMeta(ticket)

    if (!typeGroups.has(typeMeta.key)) {
      typeGroups.set(typeMeta.key, {
        key: typeMeta.key,
        label: typeMeta.label,
        isUnknown: typeMeta.isUnknown,
        tickets: [],
      })
    }

    typeGroups.get(typeMeta.key)!.tickets.push(ticket)
  })

  return Array.from(typeGroups.entries())
    .sort(([, a], [, b]) => {
      if (a.isUnknown && !b.isUnknown) return 1
      if (!a.isUnknown && b.isUnknown) return -1
      return a.label.localeCompare(b.label)
    })
    .map(([key, typeGroup]) => ({
      key,
      label: typeGroup.label,
      tickets: typeGroup.tickets,
    }))
}

const buildDateGroups = ({
  tickets,
  weekStart,
  weekEnd,
  selectedPeriodStart,
  selectedPeriodEnd,
}: TicketsTableProps): TicketDateGroup[] => {
  const ticketsByDay = new Map<string, Ticket[]>()
  const undatedTickets: Ticket[] = []

  sortTickets(tickets).forEach((ticket) => {
    const dateMeta = getTicketDateGroupMeta(ticket)

    if (dateMeta.key === "no-ticket-date") {
      undatedTickets.push(ticket)
      return
    }

    const existing = ticketsByDay.get(dateMeta.key) ?? []
    existing.push(ticket)
    ticketsByDay.set(dateMeta.key, existing)
  })

  const weekDays = eachDayOfInterval({
    start: weekStart,
    end: weekEnd,
  })
  const selectedStart = startOfDay(selectedPeriodStart)
  const selectedEnd = startOfDay(selectedPeriodEnd)
  const groups = weekDays.map((day) => {
    const key = format(day, "yyyy-MM-dd")
    const dayTickets = ticketsByDay.get(key) ?? []

    return {
      key,
      label: format(day, DATE_GROUP_LABEL_FORMAT),
      tickets: dayTickets,
      typeGroups: buildTypeGroups(dayTickets),
      isInSelectedPeriod:
        !isBefore(startOfDay(day), selectedStart) && !isAfter(startOfDay(day), selectedEnd),
    }
  })

  if (undatedTickets.length > 0) {
    groups.push({
      key: "no-ticket-date",
      label: "No ticket date",
      tickets: undatedTickets,
      typeGroups: buildTypeGroups(undatedTickets),
      isInSelectedPeriod: true,
    })
  }

  return groups
}

const TicketRows: React.FC<{ tickets: Ticket[] }> = ({ tickets }) => (
  <>
    {tickets.map((ticket) => (
      <TableRow key={ticket.id}>
        <TableCell className="whitespace-nowrap sticky left-0 z-10 border-r block bg-background">
          <div className="flex items-center gap-2">
            <span>{ticket.person || "-"}</span>
            {!ticket.people_id && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex items-center text-amber-700 cursor-help"
                    aria-label="Person record missing"
                  >
                    <AlertTriangle className="h-4 w-4" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Person is not linked because person record does not exist.
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TableCell>
        <TableCell className="whitespace-nowrap">{ticket.dsp || "-"}</TableCell>
        <TableCell className="whitespace-nowrap">{ticket.ticket_name || "-"}</TableCell>
        <TableCell className="whitespace-nowrap">{ticket.category || "-"}</TableCell>
        <TableCell className="whitespace-nowrap">{ticket.sub_category || "-"}</TableCell>
        <TableCell className="whitespace-nowrap truncate max-w-[220px]">
          {ticket.details ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block truncate">{ticket.details}</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-[90vw] whitespace-pre overflow-x-auto">
                {ticket.details}
              </TooltipContent>
            </Tooltip>
          ) : (
            "-"
          )}
        </TableCell>
        <TableCell className="whitespace-nowrap">
          {ticket.ticket_date_time
            ? format(new Date(ticket.ticket_date_time), "dd MMM yyyy, HH:mm")
            : "-"}
        </TableCell>
        <TableCell className="whitespace-nowrap">
          {ticket.amount !== null && ticket.amount !== undefined
            ? `£${Number(ticket.amount).toFixed(2)}`
            : "-"}
        </TableCell>
        <TableCell className="whitespace-nowrap">{ticket.created_by || "-"}</TableCell>
        <TableCell className="whitespace-nowrap">{ticket.updated_by || "-"}</TableCell>
        <TableCell className="whitespace-nowrap">
          {ticket.updated_at ? format(new Date(ticket.updated_at), "dd MMM yyyy, HH:mm") : "-"}
        </TableCell>
        <TableCell className="whitespace-nowrap">
          {ticket.created_at ? format(new Date(ticket.created_at), "dd MMM yyyy, HH:mm") : "-"}
        </TableCell>
        <TableCell className="whitespace-nowrap">{ticket.tracking_id || "-"}</TableCell>
        <TableCell className="whitespace-nowrap">
          <StatusBadge status={ticket.status} />
        </TableCell>
        <TableCell className="whitespace-nowrap">{ticket.route_code || "-"}</TableCell>
        <TableCell className="whitespace-nowrap">{ticket.route_cycle || "-"}</TableCell>
      </TableRow>
    ))}
  </>
)

const TicketsTable: React.FC<TicketsTableProps> = ({
  tickets,
  weekStart,
  weekEnd,
  selectedPeriodStart,
  selectedPeriodEnd,
}) => {
  const groupedTickets = React.useMemo(
    () =>
      buildDateGroups({
        tickets,
        weekStart,
        weekEnd,
        selectedPeriodStart,
        selectedPeriodEnd,
      }),
    [tickets, weekEnd, weekStart, selectedPeriodEnd, selectedPeriodStart],
  )
  const [expandedDateGroups, setExpandedDateGroups] = useState<Record<string, boolean>>({})
  const [expandedTypeGroups, setExpandedTypeGroups] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setExpandedDateGroups((prev) => {
      const next: Record<string, boolean> = {}
      groupedTickets.forEach((dateGroup) => {
        next[dateGroup.key] = dateGroup.isInSelectedPeriod ? (prev[dateGroup.key] ?? true) : false
      })
      return next
    })
  }, [groupedTickets])

  useEffect(() => {
    const groupKeys = groupedTickets.flatMap((dateGroup) =>
      dateGroup.typeGroups.map((typeGroup) => `${dateGroup.key}__${typeGroup.key}`),
    )

    setExpandedTypeGroups((prev) => {
      const next: Record<string, boolean> = {}
      groupKeys.forEach((key) => {
        next[key] = prev[key] ?? true
      })
      return next
    })
  }, [groupedTickets])

  const toggleDateGroup = (groupKey: string) => {
    const dateGroup = groupedTickets.find((group) => group.key === groupKey)
    if (!dateGroup?.isInSelectedPeriod) return

    setExpandedDateGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }))
  }

  const toggleTypeGroup = (groupKey: string) => {
    setExpandedTypeGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }))
  }

  return (
    <TooltipProvider>
      <div className="space-y-6 p-4">
        {groupedTickets.map((dateGroup) => {
          const isDateExpanded =
            dateGroup.isInSelectedPeriod && expandedDateGroups[dateGroup.key] !== false

          return (
            <div key={dateGroup.key} className="border rounded-lg overflow-hidden">
              <button
                type="button"
                disabled={!dateGroup.isInSelectedPeriod}
                className={cn(
                  "flex w-full items-center justify-between gap-4 px-4 py-3 text-left",
                  dateGroup.isInSelectedPeriod
                    ? "bg-palette-jungleGreen000"
                    : "cursor-not-allowed bg-palette-jungleGreen000/70 opacity-80",
                )}
                onClick={() => toggleDateGroup(dateGroup.key)}
              >
                <div className="flex items-center gap-3">
                  {isDateExpanded ? (
                    <ChevronDown className="h-4 w-4 text-palette-jungleGreen600" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-palette-jungleGreen600" />
                  )}
                  <div>
                    <h3 className="font-semibold text-palette-jungleGreen900">{dateGroup.label}</h3>
                    {!dateGroup.isInSelectedPeriod ? (
                      <p className="mt-1 text-xs text-palette-jungleGreen500">
                        Outside the selected period
                      </p>
                    ) : null}
                  </div>
                </div>
                <span className="text-sm text-palette-jungleGreen600">
                  {dateGroup.tickets.length} ticket{dateGroup.tickets.length === 1 ? "" : "s"}
                </span>
              </button>

              {isDateExpanded ? (
                dateGroup.tickets.length === 0 ? (
                  <div className="border-t px-4 py-8 text-sm text-palette-jungleGreen500">
                    No tickets for this day.
                  </div>
                ) : (
                  <div className="overflow-x-auto border-t">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="sticky left-0 z-10 border-r flex items-center bg-background">
                            Person
                          </TableHead>
                          <TableHead>DSP</TableHead>
                          <TableHead>Ticket Name</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Sub Category</TableHead>
                          <TableHead>Details</TableHead>
                          <TableHead>Ticket Date/Time</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Created By</TableHead>
                          <TableHead>Updated By</TableHead>
                          <TableHead>Updated At</TableHead>
                          <TableHead>Created At</TableHead>
                          <TableHead>Tracking ID</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Route Code</TableHead>
                          <TableHead>Route Cycle</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dateGroup.typeGroups.map((typeGroup) => {
                          const groupKey = `${dateGroup.key}__${typeGroup.key}`
                          const isExpanded = expandedTypeGroups[groupKey] !== false

                          return (
                            <React.Fragment key={groupKey}>
                              <TableRow
                                className="bg-palette-jungleGreen000/60 cursor-pointer hover:bg-palette-jungleGreen100"
                                onClick={() => toggleTypeGroup(groupKey)}
                              >
                                <TableCell colSpan={16}>
                                  <div className="flex items-center justify-between text-sm text-palette-jungleGreen800">
                                    <div className="flex items-center gap-2">
                                      {isExpanded ? (
                                        <ChevronDown className="h-4 w-4 text-palette-jungleGreen600" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-palette-jungleGreen600" />
                                      )}
                                      <span className="font-medium">{typeGroup.label}</span>
                                    </div>
                                    <span className="text-xs text-palette-jungleGreen500">
                                      {typeGroup.tickets.length} ticket
                                      {typeGroup.tickets.length === 1 ? "" : "s"}
                                    </span>
                                  </div>
                                </TableCell>
                              </TableRow>
                              {isExpanded ? <TicketRows tickets={typeGroup.tickets} /> : null}
                            </React.Fragment>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )
              ) : null}
            </div>
          )
        })}
      </div>
    </TooltipProvider>
  )
}

export default TicketsTable
